import { Controller } from "@hotwired/stimulus"
import { debounce } from "lodash"
import { escapeHtml, buildExcerpt, highlightQuery, formatSearchTimestamp } from "../../lib/chat_utils"

export default class extends Controller {
  static targets = ["modal", "input", "loading", "results"]

  connect() {
    this.debouncedSearch = debounce(this.performSearch.bind(this), 200)
    this.activeQuery = ''

    this.boundClose = this.close.bind(this)
    document.addEventListener('chat:close-message-search', this.boundClose)
  }

  disconnect() {
    if (this.debouncedSearch && this.debouncedSearch.cancel) {
      this.debouncedSearch.cancel()
    }
    document.removeEventListener('chat:close-message-search', this.boundClose)
  }

  open() {
    document.dispatchEvent(new CustomEvent('chat:close-metadata'))

    this.modalTarget.classList.remove('hidden')
    this.clearResults()

    if (this.hasInputTarget) {
      this.inputTarget.value = ''
      setTimeout(() => this.inputTarget.focus(), 50)
    }
  }

  close() {
    this.modalTarget.classList.add('hidden')
    if (this.debouncedSearch && this.debouncedSearch.cancel) {
      this.debouncedSearch.cancel()
    }
    this.clearResults()
  }

  handleInput(event) {
    const query = (event.target.value || '').trim()
    this.activeQuery = query

    if (query.length < 2) {
      this.hideLoading()
      this.renderHint(query)
      return
    }

    this.showLoading()
    this.debouncedSearch(query)
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
    }
  }

  async performSearch(query) {
    try {
      const response = await fetch(`/admin/chats/message_search?q=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error('Conversation search failed')

      const results = await response.json()
      if (query !== this.activeQuery) return
      this.hideLoading()
      this.renderResults(results, query)
    } catch (error) {
      console.error('Conversation search error:', error)
      this.hideLoading()
      this.renderError()
    }
  }

  selectResult(event) {
    const partnerType = event.currentTarget.dataset.partnerType
    const partnerId = Number(event.currentTarget.dataset.partnerId)
    const messageId = Number(event.currentTarget.dataset.messageId)

    if (!partnerType || Number.isNaN(partnerId) || Number.isNaN(messageId)) return

    this.close()

    document.dispatchEvent(new CustomEvent('chat:select-conversation', {
      detail: { partnerType, partnerId, messageId }
    }))
  }

  clearResults() {
    this.hideLoading()
    if (!this.hasResultsTarget) return

    this.resultsTarget.innerHTML = '<div class="text-xs text-slate-400 px-2 py-6 text-center">Search for a word or phrase in all conversations.</div>'
  }

  showLoading() {
    if (!this.hasLoadingTarget) return
    this.loadingTarget.classList.remove('hidden')
  }

  hideLoading() {
    if (!this.hasLoadingTarget) return
    this.loadingTarget.classList.add('hidden')
  }

  renderHint(query) {
    if (!this.hasResultsTarget) return

    if (!query.length) {
      this.resultsTarget.innerHTML = '<div class="text-xs text-slate-400 px-2 py-6 text-center">Search for a word or phrase in all conversations.</div>'
      return
    }

    this.resultsTarget.innerHTML = '<div class="text-xs text-slate-400 px-2 py-6 text-center">Keep typing...</div>'
  }

  renderError() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.innerHTML = '<div class="text-xs text-red-300 px-2 py-6 text-center">Failed to search messages. Please try again.</div>'
  }

  renderResults(results, query) {
    if (!this.hasResultsTarget) return

    if (!results || results.length === 0) {
      this.resultsTarget.innerHTML = `<div class="text-xs text-slate-400 px-2 py-6 text-center">No messages found for "${escapeHtml(query)}"</div>`
      return
    }

    const items = results.map((result) => {
      const partnerName = escapeHtml(result.partner_name || '')
      const excerpt = highlightQuery(buildExcerpt(result.content, query), query)
      const timestamp = formatSearchTimestamp(result.created_at)

      return `
        <button type="button"
                class="group w-full text-left px-3 py-2 hover:bg-slate-800/60 transition"
                data-partner-type="${result.partner_type}"
                data-partner-id="${result.partner_id}"
                data-message-id="${result.message_id}"
                data-action="click->admin--chat-message-search#selectResult">
          <div class="flex items-center gap-2 text-sm min-w-0">
            <span class="font-medium text-slate-200 shrink-0">${partnerName}</span>
            <span class="text-slate-500 shrink-0">-</span>
            <span class="text-slate-400 truncate min-w-0">${excerpt}</span>
            <span class="text-[11px] text-slate-500 ml-auto shrink-0 group-hover:text-slate-300 transition">${timestamp}</span>
          </div>
        </button>
      `
    }).join('')

    this.resultsTarget.innerHTML = `
      <div class="rounded-lg bg-slate-950/50 overflow-hidden divide-y divide-slate-800/80">
        ${items}
      </div>
    `
  }
}
